import { Square } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import clsx from 'clsx';

/*
 * PILL PERIMETER PARAMETERIZATION
 * ================================
 * For a 2:1 aspect-ratio element with border-[12px]:
 *   Width = W, Height = H = W/2
 *   Right-arc center at (75%, 50%), Left-arc center at (25%, 50%)
 *   Each arc radius = H/2 = W/4 = 25% of W
 *   Rail midline offset: 6px inward from outer edge
 *
 * Perimeter sections (d in units of W, starting from top-center, clockwise):
 *   [0,    0.25]      → Top-right flat  (x: 50%→75%, y: rail top)
 *   [0.25, 0.25+π/4]  → Right arc       (center 75%,50%, angles -90°→90°)
 *   [0.25+π/4, 0.75+π/4] → Bottom flat  (x: 75%→25%, y: rail bottom)
 *   [0.75+π/4, 0.75+π/2] → Left arc     (center 25%,50%, angles 90°→270°)
 *   [0.75+π/2, 1+π/2]    → Top-left flat (x: 25%→50%, y: rail top)
 *
 *   TOTAL = 1 + π/2 ≈ 2.5708 (in W units)
 *
 * Dealer occupies slot 0 (center-top).
 * N player seats are distributed at d = spacing * k, k = 1..N
 * where spacing = TOTAL / (N + 1)  →  perfectly equal gaps around the loop.
 */

const PI = Math.PI;
const TOTAL = 1 + PI / 2;     // full perimeter in W-units

const S1 = 0.25;              // end of top-right flat
const S2 = S1 + PI / 4;       // end of right arc
const S3 = S2 + 0.5;          // end of bottom flat
const S4 = S3 + PI / 4;       // end of left arc  (= TOTAL - 0.25)

// Returns the CSS style object { left, top } for a seat at perimeter distance d.
function pillStyle(d) {
    // Wrap around the full perimeter
    const t = ((d % TOTAL) + TOTAL) % TOTAL;

    if (t < S1) {
        // Top-right flat  (y = 6px from top)
        const x = 50 + (t / S1) * 25; // 50% → 75%
        return { left: `${x.toFixed(3)}%`, top: 'calc(0% + 6px)' };

    } else if (t < S2) {
        // Right arc  (center at 75%, 50%, r → rail midline)
        const frac = (t - S1) / (S2 - S1);
        const angle = -PI / 2 + frac * PI;   // -90° … +90°
        const cos = Math.cos(angle), sin = Math.sin(angle);
        return {
            left: `calc(75% + (25% - 6px) * ${cos.toFixed(6)})`,
            top: `calc(50% + (50% - 6px) * ${sin.toFixed(6)})`,
        };

    } else if (t < S3) {
        // Bottom flat  (y = 6px from bottom)
        const x = 75 - ((t - S2) / (S3 - S2)) * 50; // 75% → 25%
        return { left: `${x.toFixed(3)}%`, top: 'calc(100% - 6px)' };

    } else if (t < S4) {
        // Left arc  (center at 25%, 50%, r → rail midline)
        const frac = (t - S3) / (S4 - S3);
        const angle = PI / 2 + frac * PI;    // 90° … 270°
        const cos = Math.cos(angle), sin = Math.sin(angle);
        return {
            left: `calc(25% + (25% - 6px) * ${cos.toFixed(6)})`,
            top: `calc(50% + (50% - 6px) * ${sin.toFixed(6)})`,
        };

    } else {
        // Top-left flat  (y = 6px from top)
        const x = 25 + ((t - S4) / (TOTAL - S4)) * 25; // 25% → 50%
        return { left: `${x.toFixed(3)}%`, top: 'calc(0% + 6px)' };
    }
}

// Generate N evenly-spaced seat positions.
// Dealer is at d=0 (top-center). Seats fill the remaining arc equally.
function generateSeats(n) {
    const spacing = TOTAL / (n + 1); // gap between each slot including Dealer
    return Array.from({ length: n }, (_, i) => ({
        id: i + 1,
        style: pillStyle(spacing * (i + 1)),
    }));
}

// Dealer is always at center-top (d = 0)
const DEALER_STYLE = { left: '50%', top: 'calc(0% + 6px)' };

export const PokerTable = ({ tableData, onSeatClick, allPlayers = [], playerCount = 10, readOnly = false }) => {
    const { t } = useLanguage();
    const seatPositions = generateSeats(playerCount);      // recalculates whenever playerCount changes
    const getPlayerAtSeat = (seatId) => tableData?.seats?.[seatId] || null;
    const isOmaha = tableData?.type === 'omaha';
    const dealerPlayer = tableData?.seats?.['dealer'];
    // For tiny seat buttons — show first word of name (truncated to 10 chars max)
    const shortName = (name) => {
        if (!name) return '';
        const first = name.trim().split(/\s+/)[0];
        return first.length > 10 ? first.slice(0, 9) + '…' : first;
    };

    const seatBase = clsx(
        'absolute transform -translate-x-1/2 -translate-y-1/2',
        'transition-all duration-300 ease-in-out',
        'w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12',
        'rounded-lg sm:rounded-xl md:rounded-2xl',
        'flex flex-col items-center justify-center gap-0.5',
        'border-2 shadow-xl hover:scale-110 z-20 hover:z-30',
        'overflow-hidden',
    );

    return (
        <div className="relative w-full h-full flex items-center justify-center p-2 sm:p-4">
            <div className="relative w-full aspect-[2/1] max-h-full max-w-5xl">

                {/* Golden Rail */}
                <div className="absolute inset-0 bg-emerald-900 rounded-full border-[12px] md:border-[16px] border-amber-600/90 shadow-[0_0_80px_rgba(0,0,0,0.6),inset_0_0_100px_rgba(0,0,0,0.5)] ring-1 ring-white/10">
                    <div className="absolute inset-2 rounded-full border border-white/5 bg-gradient-to-b from-white/5 to-transparent" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none opacity-20 select-none">
                        <div className="flex gap-1 mb-2">
                            <Square className="w-6 h-8 md:w-8 md:h-10 text-amber-500 fill-amber-500/10" />
                            <Square className="w-6 h-8 md:w-8 md:h-10 text-amber-500 fill-amber-500/10" />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-black text-amber-500 uppercase tracking-tighter">HaReshima</h2>
                        <span className="text-[10px] md:text-xs font-bold text-white tracking-widest uppercase">
                            {isOmaha ? 'Omaha' : "Texas Hold'em"}
                        </span>
                    </div>
                </div>

                {/* Seats Overlay */}
                <div className="absolute inset-0">

                    {/* Player Seats */}
                    {seatPositions.map((pos) => {
                        const player = getPlayerAtSeat(pos.id);
                        const occupied = !!player;
                        const isReserved = (allPlayers || []).some(p => 
                            p.seatPreference?.hasPreference && 
                            parseInt(p.seatPreference.seatNumber) === pos.id &&
                            !Object.values(tableData?.seats || {}).some(s => s.id === p.id) // Not already seated elsewhere
                        );

                        return (
                            <button
                                key={pos.id}
                                onClick={() => !readOnly && onSeatClick(pos.id)}
                                className={clsx(seatBase,
                                    occupied
                                        ? 'bg-blue-600 border-blue-400 text-white shadow-blue-900/50'
                                        : isReserved
                                            ? 'bg-amber-500/10 border-amber-500/50 text-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                                            : 'bg-slate-900/80 backdrop-blur-sm border-slate-700 text-slate-400 hover:border-amber-500/50 hover:text-amber-500 hover:bg-slate-800'
                                )}
                                style={pos.style}
                            >
                                {occupied ? (
                                    <span className="text-[8px] sm:text-[10px] md:text-[11px] font-bold w-full mx-auto overflow-hidden text-ellipsis whitespace-nowrap px-0.5 text-center font-heebo leading-none block">
                                        {shortName(player.name)}
                                    </span>
                                ) : (
                                    <>
                                        <span className="text-sm md:text-base font-light leading-none font-inter">+</span>
                                        <span className="text-[8px] md:text-[10px] font-bold uppercase tracking-tighter opacity-60 font-inter">{pos.id}</span>
                                    </>
                                )}
                            </button>
                        );
                    })}

                    {/* Dealer — center-top */}
                    <button
                        onClick={() => !readOnly && onSeatClick('dealer')}
                        className={clsx(seatBase,
                            dealerPlayer
                                ? 'bg-amber-500 border-amber-300 text-slate-900 shadow-amber-900/50'
                                : 'bg-slate-900/90 backdrop-blur-sm border-amber-500/60 text-amber-400 hover:border-amber-400 hover:bg-slate-800'
                        )}
                        style={DEALER_STYLE}
                    >
                        {dealerPlayer ? (
                            <span className="text-[8px] sm:text-[10px] md:text-[11px] font-bold w-full mx-auto overflow-hidden text-ellipsis whitespace-nowrap px-0.5 text-center font-heebo leading-none block">
                                {shortName(dealerPlayer.name)}
                            </span>
                        ) : (
                            <>
                                <span className="text-[9px] md:text-[11px] font-black uppercase tracking-tighter font-inter leading-none">D</span>
                                <span className="text-[6px] md:text-[8px] font-bold uppercase tracking-tighter opacity-70 font-inter leading-none">Dealer</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
